import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { LayoutEngine } from "@tento-lona/sheets/platform";
import type { Option, RowLocalId } from "@tento-lona/sheets";

export namespace LayoutDebugOverlayPlan {
  export type RenderedRow = LayoutEngine.Row & {
    rowIdx: number;
  };

  export type RenderedColumn = {
    resolved: ColumnIndex.Key.Resolved.Default;
    widthPx: number;
  };

  export type Spec = {
    id: string;
    rowId: RowLocalId;
    rowIdx: number;
    colOffset: number;
    stackIndex: Option<number>;
    shape: LayoutEngine.Shape;
    spanKey: Option<string>;
    anchor: ColumnIndex.Key.Resolved.WithSubindex;
    inset: {
      left: number;
      top: number;
    };
    widthPx: number;
    heightPx: number;
    label: string;
    color: string;
    target: "fixed" | "static";
  };

  export type Op =
    | { type: "reveal"; spec: Spec }
    | { type: "update"; spec: Spec }
    | { type: "dismiss"; id: string; target: Spec["target"]; rowId: RowLocalId };

  export type Plan = {
    next: Map<string, Spec>;
    ops: Op[];
  };

  export type InvariantReport = {
    duplicateIds: string[];
    duplicateSlots: string[];
    contiguityGaps: string[];
    spanOwnerDuplicates: string[];
    stackedAlignmentIssues: string[];
  };

  export function buildSpecs(
    engine: LayoutEngine,
    sheet: LayoutEngine.Sheet,
    rows: RenderedRow[],
    columns: RenderedColumn[],
  ): Spec[] {
    const specs: Spec[] = [];

    for (const row of rows) {
      const firstColumn = columns[0];
      if (firstColumn !== undefined) {
        const firstProbe = engine.resolveCell({
          row,
          column: {
            offset: firstColumn.resolved.offset,
            widthPx: firstColumn.widthPx,
            period: firstColumn.resolved.pd,
          },
          sheet,
          query: {},
        });
        if (firstProbe.shape === "static-span") {
          const staticSpec = toStaticSpanSpec(
            row,
            firstColumn,
            firstProbe,
            sheet,
          );
          if (staticSpec) {
            specs.push(staticSpec);
          }
          continue;
        }
      }

      const spanBuckets = new Map<
        string,
        {
          startOffset: number;
          endOffset: number;
          yOffsetPx: number;
          heightPx: number;
        }
      >();

      for (const column of columns) {
        const probe = engine.resolveCell({
          row,
          column: {
            offset: column.resolved.offset,
            widthPx: column.widthPx,
            period: column.resolved.pd,
          },
          sheet,
          query: {},
        });

        if (probe.mode.type === "stacked" && row.layout.stackingInfo) {
          const visible = row.layout.stackingInfo.visibleCellCount;
          for (let stackIndex = 0; stackIndex < visible; stackIndex++) {
            const resolved = engine.resolveCell({
              row,
              column: {
                offset: column.resolved.offset,
                widthPx: column.widthPx,
                period: column.resolved.pd,
              },
              sheet,
              query: { stackIndex },
            });
            const spec = toSpec(row, column, resolved, stackIndex, visible);
            if (spec) specs.push(spec);
          }
          continue;
        }

        if (probe.mode.type === "spanned" && probe.span) {
          const spanKey = probe.span.period.toString();
          const existing = spanBuckets.get(spanKey);
          const next = {
            startOffset: existing
              ? Math.min(existing.startOffset, column.resolved.offset)
              : column.resolved.offset,
            endOffset: existing
              ? Math.max(existing.endOffset, column.resolved.offset)
              : column.resolved.offset,
            yOffsetPx: probe.frame?.yOffsetPx ?? existing?.yOffsetPx ?? 0,
            heightPx:
              probe.frame?.heightPx ??
              existing?.heightPx ??
              row.layout.cellHeightPx,
          };
          spanBuckets.set(spanKey, next);
          continue;
        }

        const spec = toSpec(row, column, probe, null, null);
        if (spec) specs.push(spec);
      }

      for (const [spanKey, bucket] of spanBuckets) {
        const spanSpec = toSpanSpec(row, columns, bucket, spanKey);
        if (spanSpec) specs.push(spanSpec);
      }
    }

    return specs;
  }

  function toSpanSpec(
    row: RenderedRow,
    columns: RenderedColumn[],
    bucket: {
      startOffset: number;
      endOffset: number;
      yOffsetPx: number;
      heightPx: number;
    },
    spanKey: string,
  ): Option<Spec> {
    const $start = columns.find(
      (column) => column.resolved.offset === bucket.startOffset,
    );
    if (!$start) return null;

    const widthPx = columns
      .filter(
        (column) =>
          bucket.startOffset <= column.resolved.offset &&
          column.resolved.offset <= bucket.endOffset,
      )
      .reduce((acc, column) => acc + column.widthPx, 0);
    if (widthPx <= 0) return null;

    return {
      id: `layout-debug-row-${sanitizeIdPart(row.id.toString())}-span-${sanitizeIdPart(spanKey)}`,
      rowId: row.id,
      rowIdx: row.rowIdx,
      colOffset: bucket.startOffset,
      stackIndex: null,
      shape: "span-owner",
      spanKey,
      anchor: new ColumnIndex.Key.Resolved.WithSubindex(
        $start.resolved,
        new Rational(0, 1),
      ),
      inset: {
        left: 0,
        top: toOverlayTop(row, bucket.yOffsetPx),
      },
      widthPx,
      heightPx: Math.max(1, Math.round(bucket.heightPx)),
      label: `r${row.rowIdx} span ${bucket.startOffset}-${bucket.endOffset}`,
      color: colorForSpec("span-owner"),
      target: "fixed",
    };
  }

  function toStaticSpanSpec(
    row: RenderedRow,
    firstColumn: RenderedColumn,
    probe: LayoutEngine.Resolution,
    sheet: LayoutEngine.Sheet,
  ): Option<Spec> {
    const periodLabel = probe.span?.period.toString() ?? "static";
    return {
      id: `layout-debug-row-${sanitizeIdPart(row.id.toString())}-static`,
      rowId: row.id,
      rowIdx: row.rowIdx,
      colOffset: firstColumn.resolved.offset,
      stackIndex: null,
      shape: "static-span",
      spanKey: probe.span?.period.toString() ?? null,
      anchor: new ColumnIndex.Key.Resolved.WithSubindex(
        firstColumn.resolved,
        new Rational(0, 1),
      ),
      inset: {
        left: 0,
        top: toOverlayTop(row, 0),
      },
      widthPx: Math.max(1, sheet.visibleWidthPx),
      heightPx: Math.max(1, Math.round(row.layout.cellHeightPx)),
      label: `r${row.rowIdx} static ${periodLabel}`,
      color: colorForSpec("static-span"),
      target: "static",
    };
  }

  export function computePlan(
    prev: Map<string, Spec>,
    nextSpecs: Spec[],
  ): Plan {
    const next = new Map<string, Spec>();
    const ops: Op[] = [];

    for (const spec of nextSpecs) {
      next.set(spec.id, spec);

      const old = prev.get(spec.id);
      if (!old) {
        ops.push({ type: "reveal", spec });
        continue;
      }

      if (!equals(old, spec)) {
        ops.push({ type: "update", spec });
      }
    }

    for (const [id, old] of prev) {
      if (!next.has(id)) {
        ops.push({ type: "dismiss", id, target: old.target, rowId: old.rowId });
      }
    }

    return { next, ops };
  }

  export function validateInvariants(
    rows: RenderedRow[],
    columns: RenderedColumn[],
    specs: Spec[],
  ): InvariantReport {
    const duplicateIds = findDuplicateIds(specs);
    const duplicateSlots = findDuplicateSlots(specs);
    const contiguityGaps = findContiguityGaps(rows, columns, specs);
    const spanOwnerDuplicates = findSpanOwnerDuplicates(specs);
    const stackedAlignmentIssues = findStackedAlignmentIssues(specs);
    return {
      duplicateIds,
      duplicateSlots,
      contiguityGaps,
      spanOwnerDuplicates,
      stackedAlignmentIssues,
    };
  }

  function toSpec(
    row: RenderedRow,
    column: RenderedColumn,
    resolution: LayoutEngine.Resolution,
    stackIndex: Option<number>,
    stackTotal: Option<number>,
  ): Option<Spec> {
    if (resolution.visibility !== "rendered" || !resolution.frame) return null;

    const numerator = resolution.shape === "stacked" ? 0 : (stackIndex ?? 0);
    const denominator = resolution.shape === "stacked" ? 1 : (stackTotal ?? 1);
    const subindex = new Rational(numerator, denominator);

    const rowKey = sanitizeIdPart(row.id.toString());
    const colKey = sanitizeIdPart(String(column.resolved.offset));
    const id =
      stackIndex == null
        ? `layout-debug-row-${rowKey}-c${colKey}`
        : `layout-debug-row-${rowKey}-c${colKey}-s${stackIndex}`;

    const label =
      stackIndex == null
        ? `r${row.rowIdx} c${column.resolved.offset}`
        : `r${row.rowIdx} c${column.resolved.offset} s${stackIndex}`;

    const spanKey =
      resolution.shape === "span-owner" && resolution.span
        ? `${resolution.span.period.toString()}:${resolution.span.ownerColumnOffset ?? "none"}`
        : null;

    return {
      id,
      rowId: row.id,
      rowIdx: row.rowIdx,
      colOffset: column.resolved.offset,
      stackIndex,
      shape: resolution.shape,
      spanKey,
      anchor: new ColumnIndex.Key.Resolved.WithSubindex(
        column.resolved,
        subindex,
      ),
      inset: {
        left: resolution.frame.xOffsetPx,
        top: toOverlayTop(row, resolution.frame.yOffsetPx),
      },
      widthPx: resolution.frame.widthPx,
      heightPx: Math.max(1, Math.round(resolution.frame.heightPx)),
      label,
      color: colorForSpec(resolution.shape),
      target: "fixed",
    };
  }

  function equals(a: Spec, b: Spec): boolean {
    return (
      a.rowId.toString() === b.rowId.toString() &&
      a.anchor.key.offset === b.anchor.key.offset &&
      a.anchor.subindex.toString() === b.anchor.subindex.toString() &&
      a.inset.left === b.inset.left &&
      a.inset.top === b.inset.top &&
      a.widthPx === b.widthPx &&
      a.heightPx === b.heightPx &&
      a.label === b.label &&
      a.color === b.color &&
      a.target === b.target
    );
  }

  function colorForSpec(shape: LayoutEngine.Shape): string {
    switch (shape) {
      case "default":
        return "var(--blue)";
      case "stacked":
        return "var(--teal)";
      case "span-owner":
        return "var(--orange)";
      case "span-hidden":
        return "var(--gray)";
      case "static-span":
        return "var(--yellow)";
    }
  }

  function toOverlayTop(row: RenderedRow, topPx: number): number {
    if (row.location === "body") {
      return topPx + 3;
    }
    return topPx;
  }

  function sanitizeIdPart(value: string): string {
    const normalized = value.replace(/[^a-zA-Z0-9_-]/g, "-");
    return normalized.length > 0 ? normalized : "x";
  }

  function findDuplicateIds(specs: Spec[]): string[] {
    const counts = new Map<string, number>();
    for (const spec of specs) {
      counts.set(spec.id, (counts.get(spec.id) ?? 0) + 1);
    }
    const duplicates: string[] = [];
    for (const [id, count] of counts) {
      if (count > 1) duplicates.push(id);
    }
    return duplicates;
  }

  function findDuplicateSlots(specs: Spec[]): string[] {
    const counts = new Map<string, number>();
    for (const spec of specs) {
      const stack = spec.stackIndex ?? -1;
      const key = `${spec.rowIdx}:${spec.shape}:${spec.colOffset}:${stack}:${spec.spanKey ?? ""}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const duplicates: string[] = [];
    for (const [key, count] of counts) {
      if (count > 1) duplicates.push(key);
    }
    return duplicates;
  }

  function findContiguityGaps(
    rows: RenderedRow[],
    columns: RenderedColumn[],
    specs: Spec[],
  ): string[] {
    const gaps: string[] = [];
    const expectedOffsets = columns.map((c) => c.resolved.offset);

    for (const row of rows) {
      const rowSpecs = specs.filter((spec) => spec.rowIdx === row.rowIdx);
      const defaultSpecs = rowSpecs.filter((spec) => spec.shape === "default");
      if (defaultSpecs.length > 0) {
        const missing = missingOffsets(
          expectedOffsets,
          defaultSpecs.map((s) => s.colOffset),
        );
        if (missing.length > 0) {
          gaps.push(`r${row.rowIdx}:default missing=${missing.join(",")}`);
        }
      }

      const stackedSpecs = rowSpecs.filter((spec) => spec.shape === "stacked");
      if (stackedSpecs.length > 0) {
        const stackGroups = new Map<number, Spec[]>();
        for (const spec of stackedSpecs) {
          const stack = spec.stackIndex ?? -1;
          const group = stackGroups.get(stack) ?? [];
          group.push(spec);
          stackGroups.set(stack, group);
        }
        for (const [stack, group] of stackGroups) {
          const missing = missingOffsets(
            expectedOffsets,
            group.map((s) => s.colOffset),
          );
          if (missing.length > 0) {
            gaps.push(
              `r${row.rowIdx}:stack=${stack} missing=${missing.join(",")}`,
            );
          }
        }
      }
    }

    return gaps;
  }

  function findSpanOwnerDuplicates(specs: Spec[]): string[] {
    const counts = new Map<string, number>();
    for (const spec of specs) {
      if (spec.shape !== "span-owner" || spec.spanKey == null) continue;
      const key = `r${spec.rowIdx}:${spec.spanKey}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const duplicates: string[] = [];
    for (const [key, count] of counts) {
      if (count > 1) duplicates.push(key);
    }
    return duplicates;
  }

  function findStackedAlignmentIssues(specs: Spec[]): string[] {
    const issues: string[] = [];
    const grouped = new Map<string, Spec[]>();
    for (const spec of specs) {
      if (spec.shape !== "stacked") continue;
      const key = `r${spec.rowIdx}:c${spec.colOffset}`;
      const group = grouped.get(key) ?? [];
      group.push(spec);
      grouped.set(key, group);
    }

    for (const [key, group] of grouped) {
      const lefts = new Set(group.map((spec) => spec.inset.left));
      const subindices = new Set(
        group.map((spec) => spec.anchor.subindex.toString()),
      );
      if (lefts.size > 1 || subindices.size > 1) {
        issues.push(
          `${key}:leftVariants=${lefts.size},subindexVariants=${subindices.size}`,
        );
      }
    }

    return issues;
  }

  function missingOffsets(expected: number[], actual: number[]): number[] {
    const actualSet = new Set(actual);
    const missing: number[] = [];
    for (const offset of expected) {
      if (!actualSet.has(offset)) {
        missing.push(offset);
      }
    }
    return missing;
  }
}
